"use client"

import React from 'react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/mixpanel';
import { DeckPlans } from './deck-plans';
import { DocumentPlans } from './document-plans';
import type { ProductType } from '@/types/cart.types';

type TabType = 'decks' | 'documents';

interface PricingPlansProps {
    activeTab: TabType;
    setActiveTab: (tab: TabType) => void;
    onAddToCart: (productType: ProductType) => void;
}

export const PricingPlans = ({ activeTab, setActiveTab, onAddToCart }: PricingPlansProps) => {

    return (
        <section id="pricing-plans-section" className={cn(
            "pt-12 pb-8 md:pt-16 md:pb-10 transition-colors duration-500 bg-white"
        )}>

            <div className="max-w-[1240px] mx-auto px-4 md:px-10">
                {/* Header */}
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What You Get</h2>
                    <p className="text-lg text-slate-600">Choose what you need, generate as many as you want. Pay only for what you create.</p>
                </div>
                {/* Tabs */}
                <div className="flex justify-center mb-16">
                    <div className="bg-slate-100 p-1.5 rounded-md flex gap-1  border border-slate-200">
                        <button
                            onClick={() => { trackEvent("Pricing Tab Viewed", { tab: "Decks" }); setActiveTab('decks'); }}
                            className={cn(
                                "px-7 py-1.5 cursor-pointer rounded-md text-[14px] transition-all duration-300",
                                activeTab === 'decks' ? "bg-white text-slate-800 font-medium" : "text-slate-500 font-medium hover:text-slate-700"
                            )}
                        >
                            Decks
                        </button>
                        <button
                            onClick={() => { trackEvent("Pricing Tab Viewed", { tab: "Documents" }); setActiveTab('documents'); }}
                            className={cn(
                                "px-7 py-1.5 cursor-pointer rounded-md text-[14px] transition-all duration-300",
                                activeTab === 'documents' ? "bg-white text-slate-800 font-medium" : "text-slate-500 font-medium hover:text-slate-700"
                            )}
                        >
                            Documents
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 transition-all">
                    {activeTab === 'decks' ? (
                        <DeckPlans onAddToCart={onAddToCart} />
                    ) : (
                        <DocumentPlans onAddToCart={onAddToCart} />
                    )}
                </div>

                {/* Footer Note */}
                <div className="mt-12 text-center">
                    <p className={cn(
                        "text-[13px] font-medium transition-colors duration-500",
                        activeTab === 'documents' ? "text-slate-400" : "text-slate-400"
                    )}>
                        {/* All prices exclusive of applicable taxes. Credits never expire. */}
                    </p>
                </div>
            </div>
        </section>
    );
};
